import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, CreditCard, HelpCircle, ChevronDown, FileText, ClipboardList, Coins } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { auth } from '../api/client';
import client from '../api/client';
import { useCredits } from '../hooks/useCredits';
import { useI18n } from '../i18n';

// Credit Gauge Component - Circular progress indicator
const CreditGauge = ({ used, total, size = 36 }) => {
  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = Math.max(total - used, 0);

  // Color based on remaining credits percentage
  const getColor = () => {
    const remainingPercent = 100 - percentage;
    if (remainingPercent <= 10) return '#ef4444'; // red
    if (remainingPercent <= 30) return '#f59e0b'; // orange
    return '#00ff88'; // brand-green
  };

  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Coins className="w-3.5 h-3.5" style={{ color: getColor() }} />
      </div>
    </div>
  );
};

const UserMenu = () => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  // Credits hook for the gauge
  const {
    loading: creditsLoading,
    summary
  } = useCredits({ autoRefresh: true, refreshInterval: 60000 });

  // Check if user has unlimited plan (superadmin, groupe, sur_mesure)
  const isUnlimitedPlan = user?.is_superuser ||
                          user?.role === 'super_admin' ||
                          user?.subscription_plan === 'superadmin' ||
                          user?.subscription_plan === 'groupe' ||
                          user?.subscription_plan === 'sur_mesure';

  // Dedicated plan uses scan quota instead of credits
  const isDedicatedPlan = user?.subscription_plan === 'dedicated';

  // Subscription data for scan quota (dedicated plan)
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    if (isDedicatedPlan) {
      client.get('/billing/subscription').then(res => {
        setSubscriptionData(res.data);
      }).catch(() => {});
    }
  }, [isDedicatedPlan]);

  // Scan quota for dedicated plan
  const scanQuotaLimit = subscriptionData?.quotas?.max_scans_per_month || 100;
  const scanQuotaUsed = subscriptionData?.usage?.current_month_scans || 0;
  const scanQuotaRemaining = Math.max(scanQuotaLimit - scanQuotaUsed, 0);
  const usesScanQuota = isDedicatedPlan && !isUnlimitedPlan;

  // Calculate credit states from summary
  const totalCredits = summary?.total_credits || 0;
  const remainingCredits = totalCredits;
  const usedCredits = (summary?.monthly_credits || 0) - remainingCredits;

  // States for display
  const isExhausted = usesScanQuota ? scanQuotaRemaining <= 0 : (!isUnlimitedPlan && remainingCredits <= 0);
  const isCritical = usesScanQuota ? (scanQuotaRemaining > 0 && scanQuotaRemaining <= 5) : (!isUnlimitedPlan && remainingCredits > 0 && remainingCredits <= 10);
  const isLow = usesScanQuota ? (scanQuotaRemaining > 5 && scanQuotaRemaining <= 20) : (!isUnlimitedPlan && remainingCredits > 10 && remainingCredits <= 50);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Check both token existence AND validity (not expired)
        if (auth.isAuthenticated() && auth.isTokenValidClientSide()) {
          const userData = await auth.getCurrentUser();
          setUser(userData);
        }
      } catch (error) {
        // Silently handle 401 - user will be redirected to login by API client
        if (error.response?.status !== 401) {
          console.error('Error fetching user data:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      // Close menu first
      setIsOpen(false);
      // Attempt logout
      await logout();
    } catch (error) {
      // Silently handle 401 - expected during logout
      if (error.response?.status !== 401) {
        console.error('Logout error:', error);
      }
    } finally {
      // Always clear token and redirect, even if API call fails
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
  };

  // Get avatar URL (returns null if no avatar, to show initials instead)
  const getAvatarUrl = () => {
    return user?.avatar_url || null;
  };

  // Get user initials (fallback if image fails to load)
  const getInitials = () => {
    if (!user) return 'U';
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Get display name
  const getDisplayName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username || user.email || 'User';
  };

  // Get role display
  const getRoleDisplay = () => {
    if (!user || !user.role) return '';
    const roleMap = {
      'super_admin': 'Super Admin',
      'company_admin': 'Admin',
      'company_user': 'User',
      'admin': 'Admin',
      'user': 'User'
    };
    return roleMap[user.role] || user.role;
  };

  // Calculate menu position
  const getMenuPosition = () => {
    if (!buttonRef.current) return { top: 0, right: 0 };
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right
    };
  };

  // Auto-logout if session is invalid (user data missing but token exists)
  useEffect(() => {
    if (!loading && auth.isAuthenticated() && !user) {
      console.warn('Invalid session detected: token present but user data missing. Automatic logout.');
      auth.logout();
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="w-10 h-10 rounded-full bg-dark-800/50 animate-pulse" />
    );
  }

  if (!auth.isAuthenticated()) {
    return (
      <Link
        to="/login"
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-green hover:text-brand-green/80 transition-colors liquid-glass rounded-lg"
      >
        <User className="w-4 h-4" />
        <span className="hidden sm:inline">{t('auth.login')}</span>
      </Link>
    );
  }

  const menuContent = isOpen && (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-64 py-2 rounded-xl border border-brand-green/20 shadow-2xl"
      style={{
        top: getMenuPosition().top,
        right: getMenuPosition().right,
        background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.98) 0%, rgba(20, 20, 20, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 0 30px rgba(0, 255, 136, 0.1), 0 10px 40px rgba(0, 0, 0, 0.5)'
      }}
    >
      {/* User Info Header */}
      <div className="px-4 py-3 border-b border-brand-green/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center text-brand-green font-bold text-sm border border-brand-green/30 overflow-hidden">
            {getAvatarUrl() ? (
              <img
                src={getAvatarUrl()}
                alt=""
                className="w-full h-full rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="text-brand-green font-bold text-sm">${getInitials()}</span>`;
                }}
              />
            ) : (
              <span className="text-brand-green font-bold text-sm">{getInitials()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{getDisplayName()}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            {getRoleDisplay() && (
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium text-brand-green bg-brand-green/10 rounded-full border border-brand-green/20">
                {getRoleDisplay()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Credit Gauge Section */}
      {!creditsLoading && (
        <Link
          to="/account?tab=subscription"
          onClick={() => setIsOpen(false)}
          className="block px-4 py-3 border-b border-brand-green/10 hover:bg-brand-green/5 transition-all"
        >
          <div className="flex items-center gap-3">
            {isUnlimitedPlan ? (
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center border border-brand-green/30">
                <Coins className="w-5 h-5 text-brand-green" />
              </div>
            ) : usesScanQuota ? (
              <CreditGauge
                used={scanQuotaUsed}
                total={scanQuotaLimit}
                size={40}
              />
            ) : (
              <CreditGauge
                used={Math.max(0, usedCredits)}
                total={Math.max(totalCredits, summary?.monthly_credits || 100)}
                size={40}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{usesScanQuota ? 'Operations this month' : t('account.subscription')}</span>
                {isUnlimitedPlan && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-brand-green/20 text-brand-green rounded">{t('account.creditsSection.statusPremium')}</span>
                )}
                {isExhausted && !isUnlimitedPlan && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500/20 text-red-400 rounded">{t('account.creditsSection.statusExhausted')}</span>
                )}
                {isCritical && !isExhausted && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500/20 text-red-400 rounded animate-pulse">{t('account.creditsSection.statusCritical')}</span>
                )}
                {isLow && !isCritical && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-yellow-500/20 text-yellow-400 rounded">{t('account.creditsSection.statusLow')}</span>
                )}
              </div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                {isUnlimitedPlan ? (
                  <span className="text-lg font-bold text-brand-green">{t('account.creditsSection.unlimited')}</span>
                ) : usesScanQuota ? (
                  <>
                    <span className={`text-lg font-bold ${
                      isExhausted ? 'text-red-400' :
                      isCritical ? 'text-red-400' :
                      isLow ? 'text-yellow-400' :
                      'text-brand-green'
                    }`}>
                      {scanQuotaRemaining}
                    </span>
                    <span className="text-xs text-gray-500">/ {scanQuotaLimit}</span>
                  </>
                ) : (
                  <>
                    <span className={`text-lg font-bold ${
                      isExhausted ? 'text-red-400' :
                      isCritical ? 'text-red-400' :
                      isLow ? 'text-yellow-400' :
                      'text-brand-green'
                    }`}>
                      {remainingCredits.toLocaleString()}
                    </span>
                    {(summary?.monthly_credits || 0) > 0 && (
                      <span className="text-xs text-gray-500">/ {(summary?.monthly_credits || 0).toLocaleString()}</span>
                    )}
                  </>
                )}
              </div>
              {/* Progress bar */}
              {(usesScanQuota || !isUnlimitedPlan) && (
                <div className="w-full h-1.5 mt-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isExhausted ? 'bg-red-500' :
                      isCritical ? 'bg-red-500' :
                      isLow ? 'bg-yellow-500' :
                      'bg-brand-green'
                    }`}
                    style={{
                      width: `${usesScanQuota
                        ? Math.max(0, Math.min(100, (scanQuotaRemaining / scanQuotaLimit) * 100))
                        : (summary?.monthly_credits > 0
                          ? Math.max(0, Math.min(100, (remainingCredits / summary.monthly_credits) * 100))
                          : (remainingCredits > 0 ? 100 : 0))}%`
                    }}
                  />
                </div>
              )}
              {isUnlimitedPlan && (
                <div className="w-full h-1.5 mt-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full w-full rounded-full bg-brand-green" />
                </div>
              )}
            </div>
          </div>
        </Link>
      )}

      {/* Menu Items */}
      <div className="py-1">
        <Link
          to="/account"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-brand-green hover:bg-brand-green/5 transition-all"
        >
          <User className="w-4 h-4" />
          <span>{t('nav.account')}</span>
        </Link>

        <a
          href="https://sylink-pentest.fr/account?tab=subscription"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-brand-green hover:bg-brand-green/5 transition-all"
        >
          <CreditCard className="w-4 h-4" />
          <span>{t('account.subscription')}</span>
        </a>

        <Link
          to="/account?tab=contract"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-brand-green hover:bg-brand-green/5 transition-all"
        >
          <FileText className="w-4 h-4" />
          <span>{t('account.contracts')}</span>
        </Link>

        <Link
          to="/account?tab=reports"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-brand-green hover:bg-brand-green/5 transition-all"
        >
          <ClipboardList className="w-4 h-4" />
          <span>{t('reports.reportType')}</span>
        </Link>

        <Link
          to="/support/tickets"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-brand-green hover:bg-brand-green/5 transition-all"
        >
          <HelpCircle className="w-4 h-4" />
          <span>{t('support.title')}</span>
        </Link>
      </div>

      {/* Logout */}
      <div className="border-t border-brand-green/10 pt-1 mt-1">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 transition-all"
        >
          <LogOut className="w-4 h-4" />
          <span>{t('nav.logout')}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 sm:gap-2 px-1.5 sm:px-2 py-1.5 rounded-lg transition-all flex-shrink-0 ${
          isOpen
            ? 'bg-brand-green/10 border-brand-green/30'
            : 'hover:bg-dark-800/50 border-transparent'
        } border`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar - Show image if available, otherwise show initials */}
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center text-brand-green font-bold text-xs border border-brand-green/30 overflow-hidden flex-shrink-0">
          {getAvatarUrl() ? (
            <img
              src={getAvatarUrl()}
              alt=""
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<span class="text-brand-green font-bold text-xs">${getInitials()}</span>`;
              }}
            />
          ) : (
            <span className="text-brand-green font-bold text-xs">{getInitials()}</span>
          )}
        </div>

        {/* Name (hidden on small screens) */}
        <span className="hidden xl:block text-sm text-gray-300 max-w-[160px] truncate" title={getDisplayName()}>
          {getDisplayName()}
        </span>

        {/* Chevron */}
        <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Portal for dropdown menu */}
      {createPortal(menuContent, document.body)}
    </>
  );
};

export default UserMenu;
